import React, { useState } from "react";
import classNames from "classnames";
import { useToggle } from "react-use";

import SuggestionInput from "shared/@forms/SuggestionInput";
import CheckboxGroup from "shared/@forms/CheckboxGroup";
import Button from "shared/Button";
import Card from "shared/Card";
import { Section } from "shared/layout";

import styles from "./index.module.css";
import { useIBOSearch } from "shared/@hooks/useIBOSearch";
import { useIBOInfo } from "shared/@hooks/useIBOInfo";
import Link from "shared/Link";
import { Modal } from "shared/Modal";
import ModalActionBar from "shared/ModalActionBar";

export default function ShareOptions({ content, methods }) {
  const [isDeleteConfirmOpen, toggleDeleteConfirmOpen] = useToggle(false);
  const [isSearchOpen, toggleSearchOpen] = useToggle(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [optimisticOwner, setOptimisticOwner] = useState(null);
  const search = useIBOSearch(searchTerm);
  const selected = useIBOInfo(content.ownerIBONumber);

  const optimisticOwnerName =
    optimisticOwner &&
    String(optimisticOwner.iboNumber) === String(content.ownerIBONumber)
      ? optimisticOwner.display.match(/\(([^()]*)\)$/)?.[1] ||
        optimisticOwner.display.replace(/^Contact:\s*/, "")
      : null;
  const ownerName =
    optimisticOwnerName ||
    (selected.isLoading
      ? "Loading owner..."
      : selected.iboInfo?.primaryInfo?.companyName ||
        `IBO #${content.ownerIBONumber}`);

  return (
    <Card>
      <Section>
        <header>
          <h3>Digital Info &amp; Retail App Only</h3>
        </header>

        <div className={styles.checkboxes}>
          <div className={styles.checkboxGroup}>
            <CheckboxGroup
              id="iboCanPreview"
              checked={content.iboCanPreview}
              onChange={() => {
                methods.setIBOCanPreview(!content.iboCanPreview);
              }}
            >
              Allow IBO to preview content?
            </CheckboxGroup>
          </div>

          <div className={styles.checkboxGroup}>
            <CheckboxGroup
              id="iboCanShare"
              checked={content.iboCanShare}
              onChange={() => {
                methods.setIBOCanShare(!content.iboCanShare);
              }}
            >
              Allow IBO to share content?
            </CheckboxGroup>
          </div>
        </div>

        <header>
          <h4>Owner IBO or Diamond</h4>
        </header>

        <div className={styles.ownerIBO}>
          {isSearchOpen || content.ownerIBONumber == null ? (
            <div className={styles.chooseOwner}>
              <SuggestionInput
                value={searchTerm}
                autoFocus={false}
                minLength={3}
                debounceTime={500}
                fetchSuggestions={setSearchTerm}
                suggestions={search.searchResults}
                isLoading={search.isLoading}
                placeholder="IBO # or Empty"
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                }}
              >
                {(item, closePanel) => {
                  return (
                    <Link
                      to="#"
                      onClick={(e) => {
                        e.preventDefault();
                        closePanel();
                        setSearchTerm("");
                        setOptimisticOwner(item);
                        methods.setOwnerIBONumber(item.iboNumber);
                        toggleSearchOpen(false);
                      }}
                    >
                      {item.display}
                    </Link>
                  );
                }}
              </SuggestionInput>

              <div className={styles.buttonGroup}>
                {content.ownerIBONumber != null && (
                  <div className={styles.button}>
                    <Button
                      onClick={() => {
                        toggleSearchOpen();
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className={styles.selectedOwner}>
              <div
                className={styles.selectedOwnerName}
                role={selected.isLoading ? "status" : undefined}
                aria-busy={selected.isLoading}
              >
                {ownerName}
              </div>

              <div className={styles.buttonGroup}>
                <Button
                  size="small"
                  purpose="delete"
                  onClick={toggleDeleteConfirmOpen}
                >
                  Remove
                </Button>

                <Button size="small" onClick={toggleSearchOpen}>
                  Change
                </Button>
              </div>
            </div>
          )}
        </div>
      </Section>

      <Modal
        title="Remove owner IBO?"
        isOpen={isDeleteConfirmOpen}
        onClose={toggleDeleteConfirmOpen}
      >
        <ModalActionBar>
          <Button onClick={toggleDeleteConfirmOpen}>Cancel</Button>

          <Button
            purpose="delete"
            onClick={() => {
              methods.setOwnerIBONumber(null);
              setOptimisticOwner(null);
              toggleDeleteConfirmOpen();
            }}
          >
            Remove Owner IBO
          </Button>
        </ModalActionBar>
      </Modal>
    </Card>
  );
}
